// @flow
import client from '../../client'
import format from '../../utils/format'
import prebookReservationBuilder from '../reservations/builders/prebook'
import ondemandReservationBuilder from '../reservations/builders/ondemand'

// Types
import type { Reservation } from '../../models/reservation'

// API Endpoints
export const USER_GET_RESERVATIONS = '/reservations/'
export const ONDEMAND_USER_GET_RESERVATIONS = '/ondemand/reservations/{0}/'
type ReservationsResult = {
  results: Array<Reservation>,
  next: ?string,
}
type Options = {
  withCancelled?: boolean,
  past?: boolean | string,
  page?: number,
}

// Constants
const PAGE_SIZE = 15
const START_PAGE = 1

// Utils
const mergeResults = (res: Array<ReservationsResult>): ReservationsResult => {
  const mergedResults = res.reduce((acc, r) => acc.concat(r.results), [])
  const firstWithNext = res.find((r) => !!r.next)
  const mergedNext = firstWithNext ? firstWithNext.next : ''
  mergedResults.sort((a, b) => a.pickupTime.isBefore(b.pickupTime))

  return {
    results: mergedResults,
    next: mergedNext,
  }
}

const handleNotFoundError = (error) => {
  // We are requesting next page for both ondemand and prebook at the same time
  // But if, for example, prebook have more result but ondemand doesnt,
  // then ondemand will return an error `not_found` and a whole request
  // will fail. That logic handles that situation and just ignore `not_found`
  if (error.response.code === 'not_found') {
    return {
      results: [],
      next: null,
    }
  }
  return Promise.reject(error)
}

/**
 * Get the current user reservations.
 * @return {Promise|Error} A resolved Promise with an Array of Objects that
 * contains the list of current reservations for the logged user or Rejected with an Error.
 */
export const getPrebookReservations = (
  data?: Options
): Promise<ReservationsResult> => {
  const { past, page, withCancelled, ...filters } = data || {}

  const query = {
    ...filters,
    ordering: '-voyage__departure_datetime',
    past: past ? 'True' : 'False',
    page: page || START_PAGE,
    page_size: PAGE_SIZE,
    include_cancellations: !!withCancelled,
  }

  return client
    .get(USER_GET_RESERVATIONS, { query })
    .then((response) => ({
      results:
        (response &&
          response.results &&
          response.results.map(prebookReservationBuilder)) ||
        [],
      next: response && response.next,
    }))
    .catch(handleNotFoundError)
}

/**
 * Returns a list of ondemand reservations for given
 * @type {Function}
 */
export const getOndemandReservations = (
  data?: Options
): Promise<ReservationsResult> => {
  const endpoints =
    data && data.past
      ? [format(ONDEMAND_USER_GET_RESERVATIONS, 'past')]
      : [
          format(ONDEMAND_USER_GET_RESERVATIONS, 'future'),
          format(ONDEMAND_USER_GET_RESERVATIONS, 'current'),
        ]

  const query = {
    page: (data && data.page) || START_PAGE,
    page_size: PAGE_SIZE,
    include_cancellations: !!data?.withCancelled,
  }

  return Promise.all(
    endpoints.map((endpoint) =>
      client
        .get(endpoint, { query })
        .then((response) => ({
          results:
            (response &&
              response.results &&
              response.results.map(ondemandReservationBuilder)) ||
            [],
          next: response && response.next,
        }))
        .catch(handleNotFoundError)
    )
  ).then(mergeResults)
}

/**
 * Returns reservations from ondemand and prebook sources.
 * @param  {Data} data
 * @return {Array<Reservation>}
 */
export const getReservations = (data?: Options) => getPrebookReservations(data)

export default getReservations
